import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import {
  Box,
  Chip,
  IconButton,
  Tooltip,
  CircularProgress,
  Pagination,
  Typography,
  Button,
  FormControl,
  Select,
  MenuItem,
  InputLabel
} from "@mui/material";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheerioButton from "../../../Components/CheerioButton";
import { eventsend } from '../../../Config/analyticsFunctions';
import SyncIcon from "@mui/icons-material/Sync";
import { getRCSTemplatesALL, deleteRCSTemplates, syncRCSTemplatesApi } from "../../../Services";

const columns = [
  { key: "templateName", label: "Template Name", width: "2fr" },
  { key: "type", label: "Type", width: "1fr" },
  { key: "createdAt", label: "Created At", width: "1fr" },
  { key: "status", label: "Status", width: "1fr" },
  { key: "actions", label: "Actions", width: "72px" },
];

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "2-digit",
    });
  } catch {
    return "—";
  }
};

const statusChip = (status) => {
  const normalized = String(status || "").toUpperCase();
  const map = {
    PENDING: { label: "Pending", color: "#9e9e9e", bg: "#f2f2f2" },
    APPROVED: { label: "Approved", color: "#1b5e20", bg: "#e8f5e9" },
    REJECTED: { label: "Rejected", color: "#b71c1c", bg: "#ffebee" },
  };
  const cfg = map[normalized] || { label: status || "—", color: "#616161", bg: "#f5f5f5" };

  return (
    <Chip
      size="small"
      label={cfg.label}
      sx={{
        height: 24,
        fontSize: 12,
        borderRadius: "999px",
        color: cfg.color,
        backgroundColor: cfg.bg,
        "& .MuiChip-label": { px: 1.5, py: 0.25 },
      }}
    />
  );
};

const typeChip = (type) => {
  const normalized = String(type || "").toLowerCase();
  const typeText = type || "—";

  const getTypeStyle = (type) => {
    switch (normalized) {
      case 'text':
        return { color: "#1565c0", bg: "#e3f2fd" };
      case 'media':
        return { color: "#e65100", bg: "#fff3e0" };
      case 'card':
        return { color: "#4527a0", bg: "#f3e5f5" };
      case 'richcard':
        return { color: "#d32f2f", bg: "#ffebee" };
      case 'carousel':
        return { color: "#388e3c", bg: "#e8f5e9" };
      default:
        return { color: "#424242", bg: "#f5f5f5" };
    }
  };

  const style = getTypeStyle(type);

  return (
    <Chip
      size="small"
      label={typeText}
      sx={{
        height: 24,
        fontSize: 12,
        borderRadius: "999px",
        color: style.color,
        backgroundColor: style.bg,
        "& .MuiChip-label": { px: 1.5, py: 0.25 },
      }}
    />
  );
};

const RCSTemplatesTable = () => {
  const authtoken = useSelector((s) => s.main.auth_token);
  const localdata = useSelector((s) => s.main.localdata);

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [totalPages, setTotalPages] = useState(0);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('all');

  const gridTemplate = useMemo(
    () => columns.map((c) => c.width).join(" "),
    []
  );

  const fetchTemplates = async (currentPage = page, currentStatus = statusFilter, currentLimit = limit) => {
    try {
      setLoading(true);
      const data = {
        senderId: localdata?.rcsUserDetails?.senderId,
        page: currentPage,
        limit: currentLimit,
        status: currentStatus
      };
      const res = await getRCSTemplatesALL(authtoken, data);

      if (res?.flag && res?.data) {
        const templateData = res.data.data || res.data.templateInfo || [];
        setRows(Array.isArray(templateData) ? templateData : []);
        setTotalPages(res.data.totalPages || 0);
        setTotal(res.data.total || 0);
      } else {
        console.log("RCS templates error", res);
        setRows([]);
        setTotalPages(0);
        setTotal(0);
      }
    } catch (e) {
      console.error(e);
      setRows([]);
      setTotalPages(0);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTemplates();
  }, [authtoken, localdata?.rcsUserDetails?.senderId, page, statusFilter, limit]);

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const handleStatusFilterChange = (event) => {
    const newStatus = event.target.value;
    setStatusFilter(newStatus);
    setPage(1);
  };

  const handleLimitChange = (event) => {
    const newLimit = event.target.value;
    setLimit(newLimit);
    setPage(1);
  };

  const handleSyncTemplates = async () => {
    setSyncing(true);
    try {
      const data = {
        senderId: localdata?.rcsUserDetails?.senderId,
        esmeAddr: localdata?.rcsUserDetails?.esmeAddr,
        botId: localdata?.rcsUserDetails?.botId,
      };

      const res = await syncRCSTemplatesApi(authtoken, data);

      if (res?.flag) {
        console.log("Templates synced successfully");
        setPage(1);
        await fetchTemplates(1, statusFilter, limit);
        res.data?.templates?.forEach((tpl) => {
          eventsend("template_synced", {
            template_id: tpl._id,
            template_type: tpl.type,
            status: tpl.status,
            timestamp: new Date().toISOString(),
            account_id: localdata?._id,
          });
        });
      } else {
        console.error("Failed to sync templates", res);
      }
    } catch (err) {
      console.error("Error syncing templates:", err);
    } finally {
      setSyncing(false);
    }
  };

  const handleDelete = async (row) => {
    if (!row?._id) return;
    setLoading(true);
    try {
      const res = await deleteRCSTemplates(authtoken, {
        templateId: row?.templateId,
        templateName: row?.templateName,
        senderId: localdata?.rcsUserDetails?.senderId
      });

      if (res?.flag) {
        await fetchTemplates(page, statusFilter, limit);
      } else {
        console.error("Failed to delete", res);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ width: "100%", paddingBlock: '24px', paddingInline: '30px' }}>
      {/* Filter and Sync Controls */}
      <Box sx={{
        mb: 2,
        display: 'flex',
        justifyContent: 'flex-end',
        alignItems: 'center',
        gap: 2
      }}>
        {/* Left side - Status Filter */}
        <FormControl size="small" sx={{ minWidth: 150 }}>
          <InputLabel id="status-filter-label">Status</InputLabel>
          <Select
            labelId="status-filter-label"
            value={statusFilter}
            onChange={handleStatusFilterChange}
            label="Status"
            disabled={loading || syncing}
            sx={{
              borderRadius: '8px',
            }}
          >
            <MenuItem value="all">All Status</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
          </Select>
        </FormControl>

        {/* Right side - Sync button */}
        <CheerioButton
          className="button"
          borderStyle={{
            height: '40px',
          }}
          textStyle={{ color: 'white', fontSize: 16, fontWeight: 600 }}
          btnText={syncing ? 'Syncing...' : 'Sync Templates'}
          onclick={handleSyncTemplates}
          backColor={'#000000'}
          disabled={syncing || loading}
        />
      </Box>

      <div
        style={{
          border: "1px solid #E6E6E6",
          borderRadius: 12,
          overflow: "hidden",
          background: "#fff",
        }}
      >
        {/* Header */}
        <div
          style={{
            display: "grid",
            gridTemplateColumns: gridTemplate,
            alignItems: "center",
            padding: "12px 16px",
            background: "#fafafa",
            borderBottom: "1px solid #EAEAEA",
            fontWeight: 600,
            fontSize: 14,
            color: "#333",
          }}
        >
          {columns.map((col) => (
            <p key={col.key} style={{ margin: 0 }}>
              {col.label}
            </p>
          ))}
        </div>

        {/* Body */}
        {loading ? (
          <div
            style={{
              padding: 24,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress size={24} />
            <p style={{ margin: "0 0 0 12px", color: "#666" }}>Loading templates…</p>
          </div>
        ) : rows.length === 0 ? (
          <div style={{ padding: 24 }}>
            <p style={{ margin: 0, color: "#666" }}>
              {statusFilter === 'all' ? 'No templates found.' : `No ${statusFilter} templates found.`}
            </p>
          </div>
        ) : (
          rows.map((row, idx) => {
            const name = row?.viTemplate?.name || "Untitled Template";
            const type = row?.viTemplate?.type;
            const created = formatDate(row?.createdAt);

            return (
              <div
                key={row?._id || idx}
                style={{
                  display: "grid",
                  gridTemplateColumns: gridTemplate,
                  alignItems: "center",
                  padding: "14px 16px",
                  borderTop: "1px solid #F1F1F1",
                  fontSize: 14,
                }}
              >
                <p
                  title={name}
                  style={{
                    margin: 0,
                    color: "#222",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {name}
                </p>

                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {typeChip(type)}
                </div>

                <p style={{ margin: 0, color: "#444" }}>{created}</p>

                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  {statusChip(row?.viTemplate?.status)}
                </div>

                <div style={{ display: "flex", alignItems: "center" }}>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => handleDelete(row)}
                      disabled={syncing}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Bottom Controls - Rows per page and Pagination */}
      {!loading && rows.length > 0 && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            mt: 2,
            px: 2,
          }}
        >
          {/* Left side - Rows per page */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color="text.secondary">
              Rows per page:
            </Typography>
            <Select
              size="small"
              value={limit}
              onChange={handleLimitChange}
              disabled={syncing}
              variant="outlined"
              sx={{
                fontSize: '14px',
                minWidth: '80px',
                height: '32px',
                borderRadius: '8px',
                '& .MuiSelect-select': {
                  padding: '4px 24px 4px 8px',
                },
              }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={25}>25</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
          </Box>

          {/* Center - Showing info */}
          <Box sx={{ flex: 1, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              Showing {((page - 1) * limit) + 1}-{Math.min(page * limit, total)} of {total} templates
              {statusFilter !== 'all' && ` (${statusFilter})`}
            </Typography>
          </Box>

          {/* Right side - Pagination */}
          {totalPages > 1 && (
            <Pagination
              count={totalPages}
              page={page}
              onChange={handlePageChange}
              color="primary"
              shape="rounded"
              showFirstButton
              showLastButton
              disabled={syncing}
            />
          )}
        </Box>
      )}
    </Box>
  );
};

export default RCSTemplatesTable;